import {
  AimOutlined,
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BookOutlined,
  BranchesOutlined,
  BugOutlined,
  BulbOutlined,
  CalculatorOutlined,
  CheckSquareOutlined,
  CloudOutlined,
  ClusterOutlined,
  CodeOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  EyeOutlined,
  FileDoneOutlined,
  FileOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FilterOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  HeartOutlined,
  HighlightOutlined,
  HomeOutlined,
  LayoutOutlined,
  MessageOutlined,
  NotificationOutlined,
  OrderedListOutlined,
  PartitionOutlined,
  PictureOutlined,
  PieChartOutlined,
  ProfileFilled,
  ProfileOutlined,
  QuestionCircleOutlined,
  RiseOutlined,
  RobotOutlined,
  RocketOutlined,
  SafetyOutlined,
  ScheduleOutlined,
  SettingOutlined,
  ShareAltOutlined,
  SkinOutlined,
  StarOutlined,
  TagOutlined,
  TagsOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
  UserSwitchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type {
  MenuDataItem,
  Settings as ProLayoutSettings,
} from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale, useAntdConfigSetter } from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';

import { App, theme as antdTheme, ConfigProvider } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  normalizeBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import {
  buildProjectMenuData,
  getProjectIdFromLocation,
  isProjectRoute,
  isTopLevelProjectMenuPath,
  PROJECT_ROUTE_PREFIX,
} from 'config/projectObjectModel';
import React from 'react';
import { sessionAdapter } from '@/adapters/sessionAdapter';
import {
  AvatarDropdown,
  NotificationIcon,
  Question,
  SelectLang,
  ThemeToggle,
} from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import TopNavBanner from '@/components/Layout/TopNavBanner';
import CustomerWorkspaceHeaderBanner from '@/modules/customer/components/CustomerWorkspaceHeaderBanner';
import {
  extractCustomerUuidFromPathname,
  isCustomerWorkspacePath,
} from '@/modules/customer/customerWorkspaceModel';
import { DOMAIN_SIDE_MENUS, type SideMenuItem } from '@/navigation/sideMenus';
import { getActiveTopNavKey } from '@/navigation/topNav';
import { buildCustomerWorkspaceMenu } from '@/navigation/workspaceMenus';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import { bindAntdAppApis } from '@/utils/antdApp';
import { t } from '@/utils/i18n';
import { navigateWithBrowserGestureSupport } from '@/utils/linkNavigation';
import defaultSettings from '../config/defaultSettings';
import { ProjectObjectHeaderBanner } from './pages/project/context';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
export const request: RequestConfig = {
  baseURL: BizAppConfig.appApiUrl || undefined,
  ...errorConfig,
};

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const TENANT_UUID_STORAGE_KEY = 'ab.currentTenantUuid';
const publicPaths = [
  loginPath,
  '/user/register',
  '/user/register-result',
  '/user/forgot-password',
  '/user/change-password',
  '/user/access-denied',
  '/callback',
];
const THEME_STORAGE_KEY = 'ab.themeMode';
const LIGHT_LOGO_SRC = '/logo-light.png';
const DARK_LOGO_SRC = '/logo-dark.png';

const _getSavedThemeMode = (): 'light' | 'dark' | null => {
  if (typeof window === 'undefined') return null;
  const mode = window.localStorage.getItem(THEME_STORAGE_KEY);
  return mode === 'light' || mode === 'dark' ? mode : null;
};

const _toNavTheme = (
  mode: 'light' | 'dark',
): NonNullable<CoreLayoutSettings['navTheme']> =>
  mode === 'dark' ? 'realDark' : 'light';

const updateFavicon = (faviconHref: string) => {
  if (typeof document === 'undefined') return;
  const linkEl = document.querySelector(
    "link[rel='icon']",
  ) as HTMLLinkElement | null;
  if (linkEl) {
    linkEl.href = faviconHref;
    return;
  }
  const newLink = document.createElement('link');
  newLink.rel = 'icon';
  newLink.href = faviconHref;
  document.head.appendChild(newLink);
};

const filterMenuTree = (
  menuData: MenuDataItem[] = [],
  predicate: (item: MenuDataItem) => boolean,
): MenuDataItem[] => {
  return menuData.reduce<MenuDataItem[]>((acc, item) => {
    const originalChildren = (item.children || item.routes) as
      | MenuDataItem[]
      | undefined;
    const filteredChildren = originalChildren
      ? filterMenuTree(originalChildren, predicate)
      : undefined;
    const shouldKeep = predicate(item) || (filteredChildren?.length || 0) > 0;
    if (!shouldKeep) {
      return acc;
    }
    const nextItem: MenuDataItem = {
      ...item,
      children: filteredChildren,
    };
    if (item.routes) {
      nextItem.routes = filteredChildren as any;
    }
    acc.push(nextItem);
    return acc;
  }, []);
};

const PROJECT_EXTERNAL_MENU_NAMES = new Set([
  'taskManagement',
  'communication',
]);

const getStaticMenuSelectedKeys = (pathname: string): string[] | undefined => {
  if (pathname.startsWith('/asset/method/library-detail')) {
    return ['/asset/method/industry-cases'];
  }

  if (pathname.startsWith('/asset/method/detail')) {
    return ['/asset/method/industry-cases'];
  }

  if (pathname.startsWith('/methodology-center/library-detail')) {
    return ['/asset/method/industry-cases'];
  }

  if (pathname.startsWith('/methodology-center/detail')) {
    return ['/asset/method/industry-cases'];
  }

  if (pathname === '/projects/tag-management') {
    return ['/projects/tag-management'];
  }

  if (pathname === '/projects') {
    return ['/projects'];
  }

  if (pathname.startsWith('/estimation-standards/')) {
    return ['/estimation-standards'];
  }

  return undefined;
};

const isProjectMenuItem = (item: MenuDataItem) => {
  const path = item.path || '';
  if (path === PROJECT_ROUTE_PREFIX || path.startsWith('/project/')) {
    return true;
  }
  return PROJECT_EXTERNAL_MENU_NAMES.has(String(item.name || ''));
};

const getPopupContainer = (triggerNode?: HTMLElement) => {
  if (!triggerNode) {
    return document.body;
  }

  return triggerNode.parentElement ?? triggerNode;
};

const AntdAppBridge: React.FC = () => {
  const { message, notification } = App.useApp();
  React.useEffect(() => {
    bindAntdAppApis(message, notification);
  }, [message, notification]);
  return null;
};

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  const isOAuthCallback =
    typeof window !== 'undefined' && window.location.pathname === '/callback';

  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    // callback 自己负责 code 换 token 和刷新会话。禁止 SessionProvider 在换 token
    // 完成前使用本地残留 token 抢跑，否则会产生一次无意义的 403。
    initialState: isOAuthCallback
      ? { currentUser: null, tenants: [], loading: false }
      : undefined,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: false,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const returnTo = consumeApplicationAccessReturnTo();
      if (returnTo) {
        window.location.replace(returnTo);
        return;
      }

      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const HeaderTitle: React.FC<{
  logo: React.ReactNode;
  appTitle: string;
  workbenchUrl?: string;
}> = ({ logo, appTitle }) => {
  return (
    <div className="app-header-title-root">
      <a
        href="/"
        className="app-header-title-main"
        onClick={(event) => navigateWithBrowserGestureSupport(event, '/')}
      >
        {logo}
        <span className="app-header-title-name">{appTitle}</span>
      </a>
    </div>
  );
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof window !== 'undefined') {
      const tenantUuid = currentUser?.tenantUuid;
      if (tenantUuid) {
        window.localStorage.setItem(TENANT_UUID_STORAGE_KEY, tenantUuid);
      } else {
        window.localStorage.removeItem(TENANT_UUID_STORAGE_KEY);
      }
    }

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    setAntdConfig((prevConfig) => {
      const prevTheme = prevConfig?.theme || {};
      const normalized = (
        Array.isArray(prevTheme.algorithm)
          ? prevTheme.algorithm
          : prevTheme.algorithm
            ? [prevTheme.algorithm]
            : []
      ).filter((item) => item !== antdTheme.defaultAlgorithm);
      const hasDarkAlgorithm = normalized.includes(antdTheme.darkAlgorithm);

      if (useDarkMode === hasDarkAlgorithm) {
        return prevConfig;
      }

      const nextAlgorithms = useDarkMode
        ? [
            ...normalized.filter((item) => item !== antdTheme.darkAlgorithm),
            antdTheme.darkAlgorithm,
          ]
        : normalized.filter((item) => item !== antdTheme.darkAlgorithm);

      const nextAlgorithm =
        nextAlgorithms.length === 1
          ? nextAlgorithms[0]
          : nextAlgorithms.length > 1
            ? nextAlgorithms
            : antdTheme.defaultAlgorithm;

      return {
        ...prevConfig,
        theme: {
          ...prevTheme,
          algorithm: nextAlgorithm,
        },
      };
    });
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

export const rootContainer = (
  container: React.ReactNode,
  {
    setInitialState,
  }: { initialState: any; setInitialState: (updater: any) => void },
) => {
  return (
    <RootSessionContainer setInitialState={setInitialState}>
      {container}
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  setInitialState: (updater: any) => void;
  children: React.ReactNode;
}> = ({ setInitialState, children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <SessionSync setInitialState={setInitialState} />
      {children}
    </SessionProvider>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  loading?: boolean;
}> {
  return {
    loading: true,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

const ROUTE_ICON_MAP: Record<string, React.ReactNode> = {
  aim: <AimOutlined />,
  api: <ApiOutlined />,
  appstore: <AppstoreOutlined />,
  audit: <AuditOutlined />,
  barChart: <BarChartOutlined />,
  book: <BookOutlined />,
  branches: <BranchesOutlined />,
  bug: <BugOutlined />,
  bulb: <BulbOutlined />,
  calculator: <CalculatorOutlined />,
  checkSquare: <CheckSquareOutlined />,
  cloud: <CloudOutlined />,
  cluster: <ClusterOutlined />,
  code: <CodeOutlined />,
  dashboard: <DashboardOutlined />,
  database: <DatabaseOutlined />,
  desktop: <DesktopOutlined />,
  eye: <EyeOutlined />,
  file: <FileOutlined />,
  fileDone: <FileDoneOutlined />,
  fileSearch: <FileSearchOutlined />,
  fileText: <FileTextOutlined />,
  filter: <FilterOutlined />,
  folder: <FolderOutlined />,
  folderOpen: <FolderOpenOutlined />,
  heartOutlined: <HeartOutlined />,
  highlight: <HighlightOutlined />,
  home: <HomeOutlined />,
  layout: <LayoutOutlined />,
  message: <MessageOutlined />,
  notification: <NotificationOutlined />,
  orderedList: <OrderedListOutlined />,
  partition: <PartitionOutlined />,
  picture: <PictureOutlined />,
  pieChart: <PieChartOutlined />,
  profile: <ProfileOutlined />,
  profileFilled: <ProfileFilled />,
  questionCircle: <QuestionCircleOutlined />,
  rise: <RiseOutlined />,
  rocket: <RocketOutlined />,
  robot: <RobotOutlined />,
  safety: <SafetyOutlined />,
  schedule: <ScheduleOutlined />,
  setting: <SettingOutlined />,
  shareAlt: <ShareAltOutlined />,
  skin: <SkinOutlined />,
  star: <StarOutlined />,
  tag: <TagOutlined />,
  tags: <TagsOutlined />,
  team: <TeamOutlined />,
  thunderbolt: <ThunderboltOutlined />,
  tool: <ToolOutlined />,
  trophy: <TrophyOutlined />,
  unorderedList: <UnorderedListOutlined />,
  userSwitch: <UserSwitchOutlined />,
  warning: <WarningOutlined />,
};

const enrichMenuIcons = (items: MenuDataItem[]): MenuDataItem[] =>
  items.map((item) => {
    const icon =
      typeof item.icon === 'string' && ROUTE_ICON_MAP[item.icon]
        ? ROUTE_ICON_MAP[item.icon]
        : item.icon;
    const originalChildren =
      item.children || (item.routes as MenuDataItem[] | undefined);
    const children = originalChildren
      ? enrichMenuIcons(originalChildren)
      : undefined;
    return { ...item, icon, children };
  });

const toMenuMessageIdSuffix = (rawKey?: string) =>
  String(rawKey || '').replace(/-([a-z])/g, (_, ch: string) =>
    ch.toUpperCase(),
  );

const mapSideMenusToMenuData = (items: SideMenuItem[]): MenuDataItem[] =>
  items.map((item) => ({
    path: item.path,
    name: t(`menu.${toMenuMessageIdSuffix(item.key)}`, item.label),
    locale: false,
    icon: item.icon,
    children: item.children ? mapSideMenusToMenuData(item.children) : undefined,
  }));

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const bannerTitle =
    initialState?.settings?.title ||
    getBrandAppName(normalizeBrandLocale(getStoredBrandLocale()));
  const isDarkTheme = initialState?.settings?.navTheme === 'realDark';
  const currentLogo = isDarkTheme ? DARK_LOGO_SRC : LIGHT_LOGO_SRC;

  React.useEffect(() => {
    updateFavicon(currentLogo);
  }, [currentLogo]);

  const updateLayoutSettings = (
    settings: Partial<ProLayoutSettings & CoreLayoutSettings>,
  ) => {
    const nextSettings = {
      ...(initialState?.settings || {}),
      ...settings,
    } as Partial<ProLayoutSettings & CoreLayoutSettings>;

    if (typeof window !== 'undefined') {
      const mode = nextSettings.navTheme === 'realDark' ? 'dark' : 'light';
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    }

    setInitialState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        settings: nextSettings,
      };
    });
  };
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(
      initialState?.settings as
        | {
            collapsed?: boolean;
          }
        | undefined
    )?.collapsed;
  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );
  const staticMenuSelectedKeys = getStaticMenuSelectedKeys(
    history.location.pathname,
  );

  return {
    ...initialState?.settings,
    title: bannerTitle,
    logo: currentLogo,
    breadcrumbRender: false,
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <NotificationIcon key="notification" />,
        <Question key="doc" />,
        <ThemeToggle key="themeToggle" />,
        <SelectLang key="selectLang" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: false,
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return false;
      }
      const pathname = history.location.pathname;
      return (
        <div className="app-shell-header">
          {dom}
          <div className="app-shell-header-center">
            {isProjectRoute(pathname) ? (
              <ProjectObjectHeaderBanner />
            ) : isCustomerWorkspacePath(pathname) ? (
              <CustomerWorkspaceHeaderBanner />
            ) : (
              <TopNavBanner />
            )}
          </div>
        </div>
      );
    },
    menuRender: (_props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      return showNav ? dom : false;
    },
    selectedKeys: staticMenuSelectedKeys,
    headerTitleRender: (logo) => (
      <HeaderTitle
        logo={logo}
        appTitle={bannerTitle}
        workbenchUrl={BizAppConfig.appWorkbenchUrl}
      />
    ),
    menuDataRender: (menuData) => {
      const pathname = history.location.pathname;
      if (isProjectRoute(pathname)) {
        const projectId = getProjectIdFromLocation(
          pathname,
          history.location.search,
        );
        if (!projectId) {
          return [];
        }
        return enrichMenuIcons(
          buildProjectMenuData(projectId) as MenuDataItem[],
        );
      }
      if (isCustomerWorkspacePath(pathname)) {
        const customerUuid = extractCustomerUuidFromPathname(pathname);
        return enrichMenuIcons(
          buildCustomerWorkspaceMenu(customerUuid).map((item) => ({
            path: item.path,
            name: item.label,
            locale: false,
            icon: item.icon,
            children: item.children?.map((child) => ({
              path: child.path,
              name: child.label,
              locale: false,
              icon: child.icon,
            })),
          })) as MenuDataItem[],
        );
      }
      const activeDomainKey = getActiveTopNavKey(pathname);
      if (activeDomainKey) {
        const items = DOMAIN_SIDE_MENUS[activeDomainKey] ?? [];
        return enrichMenuIcons(mapSideMenusToMenuData(items));
      }
      return enrichMenuIcons(
        filterMenuTree(menuData, (item) => !isProjectMenuItem(item)),
      );
    },
    menuItemRender: (item, dom) => {
      // 旧项目对象菜单里，分组子菜单有些是三段路径（如 /project/{id}/code-contribution），
      // 不能只靠路径深度判断；顶级叶节点仍交给 ProLayout 自行渲染，避免重复图标。
      const isSubMenuItem =
        !!item.path?.startsWith('/project/') &&
        !isTopLevelProjectMenuPath(item.path);
      const icon =
        isSubMenuItem && React.isValidElement(item.icon) ? (
          <span style={{ marginInlineEnd: 8, flexShrink: 0 }}>{item.icon}</span>
        ) : null;
      const inner = icon ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {icon}
          <span
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {dom}
          </span>
        </span>
      ) : (
        dom
      );
      if (item.isUrl) {
        return (
          <a href={item.path} target={item.target || '_blank'}>
            {inner}
          </a>
        );
      }
      return (
        <a
          href={item.path}
          onClick={(event) =>
            navigateWithBrowserGestureSupport(event, item.path)
          }
        >
          {inner}
        </a>
      );
    },
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <ConfigProvider getPopupContainer={getPopupContainer}>
          <App>
            <AntdAppBridge />
            {children}
            <SiderResizeHandle
              enabled={showNav && resizeEnabled}
              siderWidth={siderWidth}
              onResizeWidth={handleResizeWidth}
            />
            {isDev && (
              <SettingDrawer
                disableUrlParams
                enableDarkTheme
                settings={initialState?.settings}
                onSettingChange={updateLayoutSettings}
              />
            )}
          </App>
        </ConfigProvider>
      );
    },
  };
};
